import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ApiService } from 'app/core/api/api.service';

@Component({
    selector: 'app-account-deletion',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatCardModule,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
    ],
    templateUrl: './account-deletion.component.html',
    styleUrls: ['./account-deletion.component.scss'],
})
export class AccountDeletionComponent {
    private readonly _formBuilder = inject(FormBuilder);
    private readonly _api = inject(ApiService);
    private readonly _snackBar = inject(MatSnackBar);

    isSending = false;
    isVerifying = false;
    step: 'request' | 'verify' | 'done' = 'request';
    requestedEmail = '';
    errorMessage = '';

    readonly requestForm = this._formBuilder.group({
        email: ['', [Validators.required, Validators.email]],
    });

    readonly verifyForm = this._formBuilder.group({
        otp: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]],
    });

    sendOtp(): void {
        if (this.requestForm.invalid || this.isSending) {
            this.requestForm.markAllAsTouched();
            return;
        }

        const email = (this.requestForm.value.email || '').trim().toLowerCase();
        if (!email) {
            return;
        }

        this.isSending = true;
        this.errorMessage = '';
        this._api.post('users/delete-account/request-otp', { email }).subscribe({
            next: () => {
                this.isSending = false;
                this.requestedEmail = email;
                this.step = 'verify';
                this._snackBar.open('Verification code sent to your email.', 'Close', {
                    duration: 3000,
                });
            },
            error: () => {
                this.isSending = false;
                this._snackBar.open(
                    "We couldn't send a verification code. Please check the email and try again.",
                    'Close',
                    {
                    duration: 3000,
                    panelClass: ['error-snackbar'],
                    }
                );
            },
        });
    }

    verifyOtp(): void {
        if (this.verifyForm.invalid || this.isVerifying) {
            this.verifyForm.markAllAsTouched();
            return;
        }

        const email = this.requestedEmail || (this.requestForm.value.email || '').trim().toLowerCase();
        const otp = (this.verifyForm.value.otp || '').trim();

        this.isVerifying = true;
        this.errorMessage = '';
        this._api.post('users/delete-account/verify-otp', { email, otp }).subscribe({
            next: () => {
                this.isVerifying = false;
                this.step = 'done';
                this._snackBar.open('Your account has been deleted.', 'Close', {
                    duration: 4000,
                });
            },
            error: (error) => {
                this.isVerifying = false;
                const message =
                    error?.error?.message ||
                    'Invalid or expired code. Please request a new one.';
                this.errorMessage = message;
                const snackMessage =
                    message === 'User not found'
                        ? "User with this email doesn't exist"
                        : message;
                this._snackBar.open(snackMessage, 'Close', {
                    duration: 4000,
                    panelClass: ['error-snackbar'],
                });
            },
        });
    }

    resendOtp(): void {
        this.sendOtp();
    }

    editEmail(): void {
        this.step = 'request';
    }
}
